// A file handed to LAMP, as LAMP's message about it shows it: the kind LAMP thinks it is and who it seems to be from,
// with Yes and "It's something else" (which lists the other kinds and "None of these"); the kinds to choose from when
// LAMP can't tell or the person's words disagree; Try again after a failure; and what LAMP read, with Record it for a
// drawing, whose number, revision and customer the person confirms or corrects first; then "Develop part <drawing>
// for <customer>" in one tap. A Tally export or a sheet of masters shows its Data Import's preview, with Confirm (#503).
// A bank statement shows each line as matched, or why it matches nothing, with Record it to post the matches. A gate
// entry held for a decision asks why, then records it anyway or sends it back; a file LAMP does not read is kept
// against a customer, supplier, Item, order or Run the person names, with a line of their own words (#489).
// A customer's purchase order naming a customer or Items not on record offers to add them and draft the order; a file
// LAMP could not take further reads "Not done", never "Done", and the thread says what went wrong (#505).
// Each answer is one tap, from the keyboard too; LAMP checks it again before anything is read or saved.
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  KEPT_AGAINST,
  rupees,
  type DrawingCorrections,
  type FileKind,
  type KeptAgainst,
  type ReceivedFile,
} from "../api.js";
import { useWords } from "../i18n/words.js";
import { DataImportPreview } from "./DataImportPreview.js";

export interface ReceivedFileCardProps {
  readonly file: ReceivedFile;
  readonly busy: boolean;
  readonly onChoose: (file: ReceivedFile, kind: "yes" | "none" | FileKind) => void;
  readonly onRetry: (file: ReceivedFile) => void;
  readonly onRecord: (file: ReceivedFile, corrected?: DrawingCorrections) => void;
  readonly onDevelop: (file: ReceivedFile) => void;
  readonly onKeep: (file: ReceivedFile, keep: { object: KeptAgainst; name: string; words: string }) => void;
  readonly onSendBack: (file: ReceivedFile, reason: string) => void;
}

/** A document a statement line settles, with the paise it takes: "INV/0102 ₹590.50". */
const settled = (value: unknown): string => {
  const s = (typeof value === "object" && value !== null ? value : {}) as { number?: unknown; amount?: unknown };
  return `${String(s.number)}${typeof s.amount === "number" ? ` ${rupees(s.amount)}` : ""}`;
};

/** A value LAMP read, as a person reads it: money in rupees, what a line settles listed, everything else as written. */
const shown = (key: string, value: unknown, w?: ReturnType<typeof useWords>): string =>
  key.endsWith("Paise") && typeof value === "number"
    ? rupees(value)
    : Array.isArray(value)
      ? value.length === 0
        ? "—"
        : value.map(settled).join(", ")
      : key === "match" && typeof value === "string" && w
        ? w.fileMatch(value)
        : value === null || value === undefined
          ? "—"
          : String(value);

function Draft({ draft }: { draft: Readonly<Record<string, unknown>> }) {
  const w = useWords();
  const fields = Object.entries(draft).filter(([, v]) => !Array.isArray(v));
  const rows = Object.entries(draft).filter(([, v]) => Array.isArray(v)) as [string, Record<string, unknown>[]][];
  return (
    <section className="file-draft" aria-label={w.fileRead}>
      <dl>
        {fields.map(([key, value]) => (
          <div key={key}>
            <dt>{w.fileField(key)}</dt>
            <dd>{shown(key, value)}</dd>
          </div>
        ))}
      </dl>
      {rows.map(([key, lines]) => {
        const columns = Object.keys(lines[0] ?? {});
        return (
          <table key={key} className="lines">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c} scope="col">
                    {w.fileField(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c}>{shown(c, line[c], w)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      })}
    </section>
  );
}

/** A drawing as LAMP read it, its number, revision and customer for the person to confirm or correct, then record. */
function DrawingCheck({
  file,
  busy,
  onRecord,
}: {
  readonly file: ReceivedFile;
  readonly busy: boolean;
  readonly onRecord: ReceivedFileCardProps["onRecord"];
}) {
  const w = useWords();
  const read = file.draft ?? {};
  const said = (key: string) => (typeof read[key] === "string" ? (read[key] as string) : "");
  const [number, setNumber] = useState(said("drawingNumber"));
  const [revision, setRevision] = useState(said("revision"));
  const [customer, setCustomer] = useState(said("customer"));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    // Only what the person changed goes back: the rest is recorded as LAMP read it.
    const changed = (value: string, before: string) => (value.trim() !== before ? value.trim() : undefined);
    const corrected = {
      ...(changed(number, said("drawingNumber")) ? { number: number.trim() } : {}),
      ...(changed(revision, said("revision")) ? { revision: revision.trim() } : {}),
      ...(changed(customer, said("customer")) ? { customer: customer.trim() } : {}),
    };
    onRecord(file, Object.keys(corrected).length > 0 ? corrected : undefined);
  };
  return (
    <form className="file-draft" aria-label={w.fileRead} onSubmit={submit}>
      <dl>
        {["title", "material"].map((key) => (
          <div key={key}>
            <dt>{w.fileField(key)}</dt>
            <dd>{shown(key, read[key])}</dd>
          </div>
        ))}
      </dl>
      <p className="muted">{w.fileCheckDrawing}</p>
      <div className="row">
        <label className="field">
          {w.fileField("drawingNumber")}
          <input value={number} required maxLength={50} onChange={(e) => setNumber(e.target.value)} />
        </label>
        <label className="field">
          {w.fileField("revision")}
          <input value={revision} required maxLength={20} onChange={(e) => setRevision(e.target.value)} />
        </label>
        <label className="field">
          {w.fileField("customer")}
          <input value={customer} maxLength={200} onChange={(e) => setCustomer(e.target.value)} />
        </label>
      </div>
      <div className="acts">
        <button type="submit" className="btn btn-go" disabled={busy || number.trim() === "" || revision.trim() === ""}>
          {w.fileRecord}
        </button>
      </div>
    </form>
  );
}

/** "Keep it against…": what the file is kept against, its name or number, and the person's line about it. */
function KeepForm({
  file,
  busy,
  onKeep,
}: {
  readonly file: ReceivedFile;
  readonly busy: boolean;
  readonly onKeep: ReceivedFileCardProps["onKeep"];
}) {
  const w = useWords();
  const [object, setObject] = useState<KeptAgainst>("customer");
  const [name, setName] = useState("");
  const [words, setWords] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onKeep(file, { object, name: name.trim(), words: words.trim() });
  };
  return (
    <form className="file-draft" aria-label={w.fileKeep} onSubmit={submit}>
      <div className="row">
        <label className="field">
          {w.fileKeepAgainst}
          <select value={object} onChange={(e) => setObject(e.target.value as KeptAgainst)}>
            {KEPT_AGAINST.map((o) => (
              <option key={o} value={o}>
                {w.fileKeepObject(o)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          {w.fileKeepName}
          <input value={name} required maxLength={200} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          {w.fileKeepWords}
          <input value={words} required maxLength={500} onChange={(e) => setWords(e.target.value)} />
        </label>
      </div>
      <div className="acts">
        <button type="submit" className="btn btn-go" disabled={busy || name.trim() === "" || words.trim() === ""}>
          {w.fileKeepSubmit}
        </button>
      </div>
    </form>
  );
}

/** A gate entry held for a decision: the person says why, then records it anyway (where it can be) or sends it back. */
function HeldDecision({
  file,
  busy,
  onRecord,
  onSendBack,
}: {
  readonly file: ReceivedFile;
  readonly busy: boolean;
  readonly onRecord: ReceivedFileCardProps["onRecord"];
  readonly onSendBack: ReceivedFileCardProps["onSendBack"];
}) {
  const w = useWords();
  const [reason, setReason] = useState("");
  const why = reason.trim();
  return (
    <div className="file-draft">
      <label className="field">
        {w.fileReason}
        <input value={reason} required maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </label>
      <div className="acts">
        {file.actions.includes("record") ? (
          <button
            type="button"
            className="btn btn-go"
            disabled={busy || why === ""}
            onClick={() => onRecord(file, { reason: why })}
          >
            {w.fileRecordAnyway}
          </button>
        ) : null}
        {file.actions.includes("send_back") ? (
          <button type="button" className="btn" disabled={busy || why === ""} onClick={() => onSendBack(file, why)}>
            {w.fileSendBack}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function ReceivedFileCard({
  file,
  busy,
  onChoose,
  onRetry,
  onRecord,
  onDevelop,
  onKeep,
  onSendBack,
}: ReceivedFileCardProps) {
  const w = useWords();
  // "It's something else" opens the list of the other kinds; LAMP asking what the file is shows it at once.
  const [choosing, setChoosing] = useState(false);
  const kinds = useRef<HTMLDivElement>(null);
  // Focus follows the list as it opens, so the keyboard carries on to the kinds.
  useEffect(() => {
    if (choosing) kinds.current?.querySelector("button")?.focus();
  }, [choosing]);
  const listed = file.state === "what_is_it" || file.state === "failed" || choosing;
  // A drawing LAMP read is checked, and corrected if need be, before it is recorded.
  const checking = file.kind === "drawing" && file.actions.includes("record");
  // "Keep it against…" opens its form; a held gate entry asks why before either answer.
  const [keeping, setKeeping] = useState(false);
  const held = file.reasonNeeded === true;
  // A file LAMP could not take further is never shown as done: the thread says what went wrong (#505).
  const stopped = file.state === "failed" || file.state === "refused";
  return (
    <section className="card file-card" aria-label={w.fileCard(file.name)}>
      <header>
        <span className={file.actions.length > 0 ? "tag tag-need" : "tag tag-plain"}>
          {file.actions.length > 0 ? w.cardNeedsYou : stopped ? w.fileNotDone : w.cardDone}
        </span>
        <h3>{file.dataImport ? w.fileDataImport : file.kind ? w.fileKind(file.kind) : w.fileWhatIsIt}</h3>
      </header>
      {file.party ? <p className="muted">{w.fileFrom(file.party)}</p> : null}
      {file.dataImport ? (
        <DataImportPreview preview={file.dataImport} />
      ) : checking ? (
        <DrawingCheck file={file} busy={busy} onRecord={onRecord} />
      ) : file.draft ? (
        <Draft draft={file.draft} />
      ) : null}
      {file.kept ? (
        <p className="muted">{w.fileKept(w.fileKeepObject(file.kept.object), file.kept.label, file.kept.words)}</p>
      ) : null}
      {file.sentBack ? <p className="muted">{w.fileSentBack(file.sentBack)}</p> : null}
      {held ? <HeldDecision file={file} busy={busy} onRecord={onRecord} onSendBack={onSendBack} /> : null}
      {keeping && file.actions.includes("keep") ? <KeepForm file={file} busy={busy} onKeep={onKeep} /> : null}
      <div className="acts">
        {file.actions.includes("yes") && file.state === "confirm" ? (
          <button type="button" className="btn btn-go" disabled={busy} onClick={() => onChoose(file, "yes")}>
            {w.fileYes}
          </button>
        ) : null}
        {file.actions.includes("choose") && !listed && file.state !== "ask" ? (
          <button type="button" className="btn" disabled={busy} onClick={() => setChoosing(true)}>
            {w.fileSomethingElse}
          </button>
        ) : null}
        {file.actions.includes("retry") ? (
          <button type="button" className="btn btn-go" disabled={busy} onClick={() => onRetry(file)}>
            {w.fileRetry}
          </button>
        ) : null}
        {file.actions.includes("record") && !checking && !held ? (
          <button type="button" className="btn btn-go" disabled={busy} onClick={() => onRecord(file)}>
            {file.dataImport ? w.fileImportConfirm : file.missing ? w.chatAddThem : w.fileRecord}
          </button>
        ) : null}
        {file.actions.includes("keep") && !keeping ? (
          <button type="button" className="btn" disabled={busy} onClick={() => setKeeping(true)}>
            {w.fileKeep}
          </button>
        ) : null}
        {file.actions.includes("develop") && file.develop ? (
          <button type="button" className="btn btn-go" disabled={busy} onClick={() => onDevelop(file)}>
            {w.fileDevelop(file.develop.drawing, file.develop.customer)}
          </button>
        ) : null}
      </div>
      {file.actions.includes("choose") && (listed || file.state === "ask") ? (
        <div ref={kinds} className="acts file-kinds" role="group" aria-label={w.fileWhatIsIt}>
          {file.state === "ask" && file.kind ? (
            <button type="button" className="btn" disabled={busy} onClick={() => onChoose(file, "yes")}>
              {w.fileKind(file.kind)}
            </button>
          ) : null}
          {file.choices.map((kind) => (
            <button key={kind} type="button" className="btn" disabled={busy} onClick={() => onChoose(file, kind)}>
              {w.fileKind(kind)}
            </button>
          ))}
          <button type="button" className="btn" disabled={busy} onClick={() => onChoose(file, "none")}>
            {w.fileNone}
          </button>
        </div>
      ) : null}
    </section>
  );
}
